import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AiOutlineQuestionCircle, AiFillQuestionCircle } from 'react-icons/ai';

// The onGoogleLogin prop might be redundant if Login component directly uses useAuth for the action.
// However, to maintain a similar structure to App.tsx's expectation or for flexibility:
interface LoginProps {
  onGoogleLogin?: () => void; // Made optional as the component can fetch it itself
}

const Login: React.FC<LoginProps> = ({ onGoogleLogin }) => {
  const { signInWithGoogle, loading } = useAuth();

  const handleGoogleLogin = async () => {
    if (onGoogleLogin) {
      onGoogleLogin(); // Call prop if provided (e.g., for logging or additional parent logic)
    } else {
      await signInWithGoogle();
    }
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden">
      <img src="/favicon.png" alt="Logo" className="absolute top-4 left-4 h-8 w-auto sm:h-12 md:h-16 z-20" />
      <div
        className="absolute top-0 left-0 w-full h-full bg-cover bg-center"
        style={{ backgroundImage: 'url(/background-image.png)' }}
      />
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4 md:p-0">
        <div className="bg-white shadow-xl rounded-lg flex overflow-hidden w-[85%] max-w-[900px] border-2 border-white transform scale-90">
          <div className="w-full md:w-1/2 p-6 md:p-0 flex flex-col justify-between">
            <div className="flex-1 flex flex-col justify-center items-center">
              <img src="/favicon.png" alt="Logo" className="h-20 w-auto mb-6 md:hidden" />
              <div className="w-full max-w-sm text-center px-0 md:px-8 flex flex-col justify-center flex-1">
                <div className="mt-2 md:mt-24">
                  <p className="text-gray-600 text-base mb-3">Sign in to your F2CS Planner.</p>
                  <div className="mt-1 mb-4 md:mb-8">
                    <button 
                      type="button" 
                      onClick={handleGoogleLogin} 
                      disabled={loading}
                      className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-4 px-6 rounded-lg transition duration-150 ease-in-out flex items-center justify-center shadow-md disabled:opacity-50 text-base"
                    >
                      <svg className="w-6 h-6 mr-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48px" height="48px">
                        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C39.712,36.404,44,30.798,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                      </svg>
                      {loading ? 'Signing In...' : 'Sign in with Google'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full px-4 md:px-8 pb-4 md:pb-8">
              <p className="text-sm sm:text-base text-gray-600 text-center font-medium">
              Sign in now. Because who knew a planner upgrade was all it took for perfect mental health?
              </p>
            </div>
          </div>
          <div className="hidden md:flex md:w-1/2 items-center justify-center bg-white">
            <img src="/logo.png" alt="FFCS Planner Illustration" className="max-w-full h-auto" />
          </div>
        </div>
      </div>

      {/* Floating Help Button with Permanent Tooltip */}
      <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 md:right-8 lg:right-12 z-50">
        <div className="relative">
          <a
            href="https://aneezhussain.com/how-to-use-f2cs-planner"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <button
              className="bg-black hover:bg-gray-900 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 ring-[1.5px] ring-white ring-offset-2 ring-offset-black"
            >
              <AiFillQuestionCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </button>

            {/* Permanent Tooltip */}
            <div className="absolute top-1/2 right-full transform -translate-y-1/2 -translate-x-6 bg-white text-black px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap max-w-[90vw] sm:max-w-none"
              style={{
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}>
              How to use?
              <div className="absolute top-1/2 right-[-6px] transform -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-white border-y-[6px] border-y-transparent"></div>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login; 
